/** 订单 Mock数据 */

import type { Order } from '../types';
import { OrderStatus, OrderDisplayStatus } from '../types';
import type { TradeOrderStats } from '../types';
import { randomPick, randomTimestamp } from '../utils/mockHelper';
import { PORTS, TRADE_TERMS } from '../utils/constants';

const buyers = [
  { id: 'BUY001', name: 'FreshMart UAE' },
  { id: 'BUY002', name: 'EuroGrocers Ltd.' },
  { id: 'BUY003', name: 'TokyoFresh Co.' },
  { id: 'BUY004', name: 'GlobalSpice LLC' },
  { id: 'BUY005', name: 'MiddleEast Foods' },
  { id: 'BUY006', name: 'Pacific Rim Imports' },
  { id: 'BUY007', name: 'Nordic Food Group' },
  { id: 'BUY008', name: 'African Trade Hub' },
  { id: 'BUY009', name: 'LatinFresh S.A.' },
  { id: 'BUY010', name: 'Southeast Asia Foods' },
  { id: 'BUY011', name: 'Australian Wholesale' },
  { id: 'BUY012', name: 'Canadian Grocers Co.' },
];

const products = [
  { name: '大蒜', category: '冷冻蔬菜', unit: 'T', specifications: ['5.5cm+', '5.0cm+', '4.5cm+', '6.0cm+'] },
  { name: '大姜', category: '冷冻蔬菜', unit: 'T', specifications: ['200g+', '300g+', '一级', '特级'] },
  { name: '智利车厘子', category: '新鲜水果', unit: 'T', specifications: ['J级', 'JJ级', 'JJJ级'] },
  { name: '蓝莓', category: '新鲜水果', unit: 'T', specifications: ['12mm+', '14mm+', '16mm+'] },
  { name: '咖啡豆', category: '咖啡豆', unit: 'T', specifications: ['AA级', 'AAA级', '精选'] },
  { name: '乳制品', category: '乳制品', unit: 'T', specifications: ['全脂', '脱脂', '半脱脂'] },
  { name: '厄瓜多尔香蕉', category: '新鲜水果', unit: 'T', specifications: ['一级', '特级', '精选'] },
  { name: '牛肉', category: '肉类', unit: 'T', specifications: ['A级', 'AA级', 'Premium'] },
  { name: '三文鱼', category: '海鲜', unit: 'T', specifications: ['4-5kg', '5-6kg', '6kg+'] },
  { name: '茶叶', category: '茶叶', unit: 'T', specifications: ['一级', '特级', '精选'] },
  { name: '杏仁', category: '坚果', unit: 'T', specifications: ['大粒', '中粒', '小粒'] },
  { name: '大米', category: '谷物', unit: 'T', specifications: ['一级', '特级', '有机'] },
];

const statuses: OrderStatus[] = [
  OrderStatus.PENDING, OrderStatus.CONFIRMED, OrderStatus.SHIPPING,
  OrderStatus.EXPORT_CUSTOMS, OrderStatus.IMPORT_CUSTOMS, OrderStatus.DELIVERED, OrderStatus.COMPLETED,
  OrderStatus.CANCELLED,
];

/** displayStatus 概率分布 */
const displayStatusPool: OrderDisplayStatus[] = [
  OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL,
  OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL,
  OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL, OrderDisplayStatus.NORMAL,
  OrderDisplayStatus.COMPLETED, OrderDisplayStatus.COMPLETED, OrderDisplayStatus.COMPLETED,
  OrderDisplayStatus.PENDING_SIGN, OrderDisplayStatus.PENDING_SIGN,
  OrderDisplayStatus.NODE_TIMEOUT,
  OrderDisplayStatus.DOCUMENTS_PENDING,
  OrderDisplayStatus.TEMPERATURE_ALERT,
  OrderDisplayStatus.IOT_OFFLINE,
];

const routes: string[] = [
  '青岛 → 迪拜', '上海 → 洛杉矶', '宁波 → 鹿特丹',
  '深圳 → 汉堡', '青岛 → 新加坡', '上海 → 迪拜',
];

const nodeList: { nodeName: string; color: string }[] = [
  { nodeName: '签约', color: '#43A047' },
  { nodeName: '备货', color: '#43A047' },
  { nodeName: '装柜', color: '#43A047' },
  { nodeName: '海运', color: '#43A047' },
  { nodeName: '到港', color: '#43A047' },
  { nodeName: '进口清关', color: '#43A047' },
  { nodeName: '配送签收', color: '#43A047' },
];

const currencies = ['USD', 'EUR', 'CNY'];

let containerSeq = 2600;

function generateContainerMapping(): { containerCount: number; containerNos: string[] } {
  const count = Math.random() < 0.3 ? 2 : 1;
  const nos: string[] = [];
  for (let i = 0; i < count; i++) {
    containerSeq += 1;
    nos.push(`BLU-C-${containerSeq}`);
  }
  return { containerCount: count, containerNos: nos };
}

/** 根据 displayStatus 确定节点颜色 */
function getNodeColor(displayStatus: OrderDisplayStatus): string {
  if (displayStatus === OrderDisplayStatus.TEMPERATURE_ALERT || displayStatus === OrderDisplayStatus.IOT_OFFLINE) {
    return '#E53935'; // alert red
  }
  if (displayStatus === OrderDisplayStatus.NODE_TIMEOUT || displayStatus === OrderDisplayStatus.DOCUMENTS_PENDING) {
    return '#FB8C00'; // warning orange
  }
  return '#43A047'; // normal green
}

function generateOrder(index: number): Order {
  const buyer = buyers[index % buyers.length];
  const product = products[index % products.length];
  const originPort = randomPick(PORTS.slice(0, 10));
  const destinationPort = randomPick(PORTS.slice(10));
  const quantity = (index + 1) * (500 + Math.floor(Math.random() * 500));
  const unitPrice = parseFloat((Math.random() * 25 + 3).toFixed(2));
  const status = statuses[index % statuses.length];
  const currency = status === OrderStatus.COMPLETED ? 'USD' : randomPick(currencies);
  const createdAt = randomTimestamp(60);
  const displayStatus = displayStatusPool[index % displayStatusPool.length];
  const specification = randomPick(product.specifications);
  const route = routes[index % routes.length];
  const containerMapping = generateContainerMapping();
  const baseNode = nodeList[index % nodeList.length];
  const currentNode = { nodeName: baseNode.nodeName, color: getNodeColor(displayStatus) };

  // 订单节点名 → 10节点编号映射
  const NODE_NAME_TO_ID: Record<string, number> = {
    '签约': 1, '备货': 2, '包装': 3, '装柜': 4, '出口报关': 5, '海运': 6, '到港': 7, '进口清关': 8, '海外仓': 9, '配送签收': 10,
  };
  const currentNodeNumber = NODE_NAME_TO_ID[baseNode.nodeName] ?? 1;

  // 付款方式分布
  const paymentMethods = ['T/T', 'T/T', 'T/T', 'L/C', 'BluCredit', 'D/P'];
  const paymentMethod = paymentMethods[index % paymentMethods.length];

  // ETD/ETA 生成
  const etdDate = new Date(Date.now() + (index + 1) * 3 * 86400000);
  const etaDate = new Date(etdDate.getTime() + (15 + Math.floor(Math.random() * 20)) * 86400000);
  const totalAmount = parseFloat((quantity * unitPrice).toFixed(2));

  return {
    id: `ORD${String(index + 1).padStart(3, '0')}`,
    orderNo: `BLU-2607-${String(index + 1).padStart(4, '0')}`,
    buyerId: buyer.id,
    buyerName: buyer.name,
    productName: product.name,
    category: product.category,
    quantity,
    unit: product.unit,
    unitPrice,
    totalAmount,
    currency,
    status,
    createdAt,
    updatedAt: new Date(new Date(createdAt).getTime() + Math.random() * 7 * 86400000).toISOString(),
    containerIds: containerMapping.containerNos,
    tradeTerms: randomPick(TRADE_TERMS),
    originPort,
    destinationPort,
    specification,
    route,
    containerMapping,
    currentNode,
    currentNodeNumber,
    paymentMethod,
    etd: etdDate.toISOString(),
    eta: etaDate.toISOString(),
    displayStatus,
    // 60%的订单需要唛头
    shippingMark: Math.random() < 0.6 ? (() => {
      const pieces = Math.ceil(quantity / 1000);
      const net = Math.round(quantity * (0.92 + Math.random() * 0.06) * 10) / 10;
      const gross = Math.round(net * (1.05 + Math.random() * 0.1) * 10) / 10;
      return {
        required: true,
        text: buyer.name.split(' ')[0],
        logoUrl: '',
        pieces,
        netWeight: net,
        grossWeight: gross,
        destination: destinationPort,
        orderNo: `BLU-2607-${String(index + 1).padStart(4, '0')}`,
        origin: '中国',
      };
    })() : undefined,
    // T/T 付款方式生成付款拆分（自由比例，无折扣）
    paymentSplit: paymentMethod === 'T/T' ? (() => {
      const preset = randomPick([{ deposit: 30, balance: 70 }, { deposit: 50, balance: 50 }, { deposit: 100, balance: 0 }]);
      return {
        depositPercent: preset.deposit,
        balancePercent: preset.balance,
        depositAmount: Math.round(totalAmount * preset.deposit / 100 * 100) / 100,
        balanceAmount: Math.round(totalAmount * preset.balance / 100 * 100) / 100,
      };
    })() : undefined,
  };
}

export const mockOrders: Order[] = Array.from({ length: 28 }, (_, i) => generateOrder(i));

/** 从订单数组聚合计算交易订单统计数据 */
export function generateTradeOrderStats(orders: Order[]): TradeOrderStats {
  const anomalyStatuses: OrderDisplayStatus[] = [
    OrderDisplayStatus.TEMPERATURE_ALERT,
    OrderDisplayStatus.IOT_OFFLINE,
    OrderDisplayStatus.NODE_TIMEOUT,
    OrderDisplayStatus.DOCUMENTS_PENDING,
  ];

  // 「本月」口径：按订单创建时间落在当前自然月过滤（前端本地时区）
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthlyOrders = orders.filter((o) => {
    const d = new Date(o.createdAt ?? '');
    return !Number.isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month;
  });
  const signedCount = monthlyOrders.filter((o) => o.displayStatus !== OrderDisplayStatus.PENDING_SIGN).length;
  const pendingReviewCount = monthlyOrders.filter((o) => o.displayStatus === OrderDisplayStatus.PENDING_SIGN).length;
  const piPendingFixCount = orders.filter((o) => o.displayStatus === OrderDisplayStatus.DOCUMENTS_PENDING).length;
  const anomalyCount = orders.filter((o) => anomalyStatuses.includes(o.displayStatus)).length;
  const oversoldCount = orders.filter((o) => o.displayStatus === OrderDisplayStatus.TEMPERATURE_ALERT).length;
  const docIncompleteCount = orders.filter((o) => o.displayStatus === OrderDisplayStatus.DOCUMENTS_PENDING).length;
  const totalContainerMappings = orders.reduce((sum, o) => sum + o.containerMapping.containerCount, 0);
  const multiOrderCount = orders.filter((o) => o.containerMapping.containerCount > 1).length;
  // 真实拼柜口径：同一柜号被 ≥2 个订单引用（LCL 拼箱），统计这样的柜号数
  const containerOrderCount = new Map<string, number>();
  for (const o of orders) {
    for (const no of o.containerMapping.containerNos) {
      containerOrderCount.set(no, (containerOrderCount.get(no) ?? 0) + 1);
    }
  }
  const sharedContainerCount = [...containerOrderCount.values()].filter((c) => c > 1).length;

  return {
    monthlyOrderCount: monthlyOrders.length,
    signedCount,
    pendingReviewCount,
    piComplianceRate: orders.length > 0
      ? Math.round(((orders.length - piPendingFixCount) / orders.length) * 1000) / 10
      : 100,
    piPendingFixCount,
    anomalyCount,
    oversoldCount,
    docIncompleteCount,
    totalContainerMappings,
    multiOrderCount,
    sharedContainerCount,
  };
}

/** 基于 mockOrders 预计算的统计数据 */
export const mockTradeOrderStats: TradeOrderStats = generateTradeOrderStats(mockOrders);

// ===== D07 BluGuard 理赔规则引擎 Mock =====

import type { ClaimWorkOrder, IotEvidence, ClaimStats } from '../types';

/** 固定 5 条 IoT 证据链（温度超限场景） */
export const mockIotEvidences: IotEvidence[] = [
  {
    id: 'IOT-EV-001',
    timestamp: '2024-12-20T14:35:00Z',
    eventType: '温度读数',
    description: '温度读数 6.2°C，超过阈值 4°C',
    deviceNo: 'IOT-BP-2001',
    readingValue: '6.2°C',
    status: 'danger',
  },
  {
    id: 'IOT-EV-002',
    timestamp: '2024-12-20T14:40:00Z',
    eventType: 'BluGuard触发',
    description: '持续超限 5min，触发 BluGuard 预案',
    deviceNo: 'IOT-BP-2001',
    readingValue: '6.5°C',
    status: 'danger',
  },
  {
    id: 'IOT-EV-003',
    timestamp: '2024-12-20T15:00:00Z',
    eventType: '温度恢复',
    description: '温度降至 3.8°C，恢复至阈值内',
    deviceNo: 'IOT-BP-2001',
    readingValue: '3.8°C',
    status: 'normal',
  },
  {
    id: 'IOT-EV-004',
    timestamp: '2024-12-20T15:30:00Z',
    eventType: '货柜确认',
    description: '货柜 MSCU-8123456 确认温度异常',
    deviceNo: 'IOT-BP-2001',
    readingValue: '异常确认',
    status: 'warning',
  },
  {
    id: 'IOT-EV-005',
    timestamp: '2024-12-21T09:00:00Z',
    eventType: '定损启动',
    description: '定损评估启动，关联 CLM-2024-0001',
    deviceNo: 'IOT-BP-2001',
    readingValue: '已启动',
    status: 'normal',
  },
];

/** 10 条理赔工单 Mock 数据 */
export const mockClaimWorkOrders: ClaimWorkOrder[] = [
  {
    id: 'CLM-001',
    clmNo: 'CLM-2024-0001',
    orderNo: 'ORD-089',
    claimType: 'temperature_exceeded',
    claimAmount: 12500,
    currency: 'USD',
    liabilityResult: 'carrier_60',
    status: 'assessing',
    createdAt: '2025-01-10T08:00:00Z',
    updatedAt: '2025-01-15T10:30:00Z',
    operatorName: '张伟',
    containerNo: 'MSCU-8123456',
    vesselName: 'MSC AURORA',
    voyageNo: 'MSC-2024-089',
    description: '冷箱运输过程中温度持续超过4°C阈值，货物品质受损',
    iotEvidences: [...mockIotEvidences],
  },
  {
    id: 'CLM-002',
    clmNo: 'CLM-2024-0002',
    orderNo: 'ORD-102',
    claimType: 'cargo_damage',
    claimAmount: 8200,
    currency: 'USD',
    liabilityResult: 'pending_liability',
    status: 'assessing',
    createdAt: '2025-01-08T14:20:00Z',
    updatedAt: '2025-01-14T09:15:00Z',
    operatorName: '李娜',
    containerNo: 'MSCU-7123001',
    vesselName: 'CMA CGM LIBRA',
    voyageNo: 'CMG-2024-102',
    description: '到港卸货时发现外包装破损，部分货物变质',
    iotEvidences: [
      {
        id: 'IOT-EV-101', timestamp: '2025-01-12T06:00:00Z',
        eventType: '货损报告', description: '卸货时发现外包装破损，拍照留证',
        deviceNo: 'N/A', readingValue: '破损', status: 'danger',
      },
      {
        id: 'IOT-EV-102', timestamp: '2025-01-12T08:30:00Z',
        eventType: '定损启动', description: '启动货损定损流程，关联 CLM-2024-0002',
        deviceNo: 'N/A', readingValue: '已启动', status: 'warning',
      },
      {
        id: 'IOT-EV-103', timestamp: '2025-01-13T10:00:00Z',
        eventType: '照片验证', description: '上传货物损坏照片至理赔系统',
        deviceNo: 'N/A', readingValue: '已上传', status: 'normal',
      },
      {
        id: 'IOT-EV-104', timestamp: '2025-01-13T16:00:00Z',
        eventType: '初步评估', description: '货损率约 15%，预计赔付 $8,200',
        deviceNo: 'N/A', readingValue: '评估中', status: 'warning',
      },
      {
        id: 'IOT-EV-105', timestamp: '2025-01-14T09:15:00Z',
        eventType: '审核流转', description: '定损报告提交审核，待定责确认',
        deviceNo: 'N/A', readingValue: '待审核', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-003',
    clmNo: 'CLM-2024-0003',
    orderNo: 'ORD-056',
    claimType: 'iot_offline',
    claimAmount: 5800,
    currency: 'USD',
    liabilityResult: 'carrier_60',
    status: 'approving',
    createdAt: '2025-01-06T11:00:00Z',
    updatedAt: '2025-01-13T14:45:00Z',
    operatorName: '王强',
    containerNo: 'MSCU-5200912',
    vesselName: 'MSC AURORA',
    voyageNo: 'MSC-2024-056',
    description: '海运途中IoT设备离线超过12小时，无法监控温度数据',
    iotEvidences: [
      {
        id: 'IOT-EV-201', timestamp: '2025-01-07T03:00:00Z',
        eventType: 'IoT离线', description: '设备 IOT-BP-2005 离线，最后心跳 03:00 UTC',
        deviceNo: 'IOT-BP-2005', readingValue: '离线', status: 'danger',
      },
      {
        id: 'IOT-EV-202', timestamp: '2025-01-07T06:00:00Z',
        eventType: 'BluGuard触发', description: '离线超过 3h，触发 BluGuard 缺失数据预案',
        deviceNo: 'IOT-BP-2005', readingValue: '预案触发', status: 'danger',
      },
      {
        id: 'IOT-EV-203', timestamp: '2025-01-07T15:00:00Z',
        eventType: '设备恢复', description: '设备恢复上线，数据回传补全',
        deviceNo: 'IOT-BP-2005', readingValue: '已恢复', status: 'warning',
      },
      {
        id: 'IOT-EV-204', timestamp: '2025-01-08T09:00:00Z',
        eventType: '数据校验', description: '回传数据显示离线期间温度在 3.2–4.8°C 波动',
        deviceNo: 'IOT-BP-2005', readingValue: '3.2–4.8°C', status: 'warning',
      },
      {
        id: 'IOT-EV-205', timestamp: '2025-01-09T11:00:00Z',
        eventType: '审批流转', description: '理赔申请已提交，审批中 — 关联 CLM-2024-0003',
        deviceNo: 'IOT-BP-2005', readingValue: '审批中', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-004',
    clmNo: 'CLM-2024-0004',
    orderNo: 'ORD-201',
    claimType: 'shortage',
    claimAmount: 3100,
    currency: 'USD',
    liabilityResult: 'platform_100',
    status: 'pending_payout',
    createdAt: '2025-01-12T09:30:00Z',
    updatedAt: '2025-01-16T11:00:00Z',
    operatorName: '赵丽',
    containerNo: 'MSCU-9382710',
    vesselName: 'CMA CGM LIBRA',
    voyageNo: 'CMG-2024-201',
    description: '实际收货数量少于提单数量，短少 15 箱',
    iotEvidences: [
      {
        id: 'IOT-EV-301', timestamp: '2025-01-14T08:00:00Z',
        eventType: '短装报告', description: '到仓验收时发现实际数量少于提单，短少 15 箱',
        deviceNo: 'N/A', readingValue: '短少15箱', status: 'danger',
      },
      {
        id: 'IOT-EV-302', timestamp: '2025-01-14T10:30:00Z',
        eventType: '铅封校验', description: '铅封完好，排除运输途中盗窃可能',
        deviceNo: 'N/A', readingValue: '铅封完好', status: 'normal',
      },
      {
        id: 'IOT-EV-303', timestamp: '2025-01-14T14:00:00Z',
        eventType: '装箱记录调取', description: '调取装箱监控记录，确认装箱数量正确',
        deviceNo: 'N/A', readingValue: '装箱正常', status: 'normal',
      },
      {
        id: 'IOT-EV-304', timestamp: '2025-01-15T09:00:00Z',
        eventType: '定责确认', description: '平台承担全责，赔付 $3,100',
        deviceNo: 'N/A', readingValue: '平台100%', status: 'warning',
      },
      {
        id: 'IOT-EV-305', timestamp: '2025-01-16T11:00:00Z',
        eventType: '待赔付', description: '赔付审批通过，等待财务打款',
        deviceNo: 'N/A', readingValue: '待打款', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-005',
    clmNo: 'CLM-2024-0005',
    orderNo: 'ORD-033',
    claimType: 'temperature_exceeded',
    claimAmount: 9400,
    currency: 'USD',
    liabilityResult: 'carrier_60',
    status: 'settled_paid',
    createdAt: '2025-01-02T07:00:00Z',
    updatedAt: '2025-01-12T16:00:00Z',
    operatorName: '张伟',
    containerNo: 'MSCU-4456789',
    vesselName: 'MAERSK SELANDIA',
    voyageNo: 'MAE-2024-033',
    description: '冷箱运输温度异常，部分水果品质下降',
    iotEvidences: [
      {
        id: 'IOT-EV-401', timestamp: '2025-01-03T12:00:00Z',
        eventType: '温度超限', description: '温度升至 5.8°C，超过阈值 4°C',
        deviceNo: 'IOT-BP-3020', readingValue: '5.8°C', status: 'danger',
      },
      {
        id: 'IOT-EV-402', timestamp: '2025-01-03T12:30:00Z',
        eventType: 'BluGuard触发', description: '持续超限触发预案',
        deviceNo: 'IOT-BP-3020', readingValue: '预案触发', status: 'danger',
      },
      {
        id: 'IOT-EV-403', timestamp: '2025-01-04T08:00:00Z',
        eventType: '温度恢复', description: '温度恢复正常范围',
        deviceNo: 'IOT-BP-3020', readingValue: '3.5°C', status: 'normal',
      },
      {
        id: 'IOT-EV-404', timestamp: '2025-01-06T10:00:00Z',
        eventType: '定损评估', description: '定损评估完成，船公司承担 60% 责任',
        deviceNo: 'IOT-BP-3020', readingValue: '船公司60%', status: 'warning',
      },
      {
        id: 'IOT-EV-405', timestamp: '2025-01-12T16:00:00Z',
        eventType: '赔付完成', description: '赔付 $9,400 已打入客户账户',
        deviceNo: 'IOT-BP-3020', readingValue: '已赔付', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-006',
    clmNo: 'CLM-2024-0006',
    orderNo: 'ORD-078',
    claimType: 'cargo_damage',
    claimAmount: 7150,
    currency: 'USD',
    liabilityResult: 'platform_100',
    status: 'settled_paid',
    createdAt: '2025-01-04T13:00:00Z',
    updatedAt: '2025-01-11T15:30:00Z',
    operatorName: '李娜',
    containerNo: 'MSCU-2891345',
    vesselName: 'COSCO PRIDE',
    voyageNo: 'COS-2024-078',
    description: '货物在目的港卸船时发现受潮，包装箱变形',
    iotEvidences: [
      {
        id: 'IOT-EV-501', timestamp: '2025-01-05T09:00:00Z',
        eventType: '货损报告', description: '卸船时发现纸箱受潮变形',
        deviceNo: 'N/A', readingValue: '受潮', status: 'danger',
      },
      {
        id: 'IOT-EV-502', timestamp: '2025-01-05T14:00:00Z',
        eventType: '湿度检测', description: '柜内湿度读数 92%，远超正常范围',
        deviceNo: 'IOT-BP-4010', readingValue: '92%', status: 'danger',
      },
      {
        id: 'IOT-EV-503', timestamp: '2025-01-06T10:00:00Z',
        eventType: '定损启动', description: '启动理赔流程，平台自查冷柜维保记录',
        deviceNo: 'IOT-BP-4010', readingValue: '已启动', status: 'warning',
      },
      {
        id: 'IOT-EV-504', timestamp: '2025-01-08T11:00:00Z',
        eventType: '定责确认', description: '平台全责 — 冷柜制冷系统维保不足',
        deviceNo: 'IOT-BP-4010', readingValue: '平台100%', status: 'warning',
      },
      {
        id: 'IOT-EV-505', timestamp: '2025-01-11T15:30:00Z',
        eventType: '赔付完成', description: '赔付 $7,150 已到账',
        deviceNo: 'IOT-BP-4010', readingValue: '已赔付', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-007',
    clmNo: 'CLM-2024-0007',
    orderNo: 'ORD-115',
    claimType: 'temperature_exceeded',
    claimAmount: 6300,
    currency: 'USD',
    liabilityResult: 'pre_loading_40',
    status: 'settled_paid',
    createdAt: '2025-01-03T06:30:00Z',
    updatedAt: '2025-01-10T12:00:00Z',
    operatorName: '张伟',
    containerNo: 'MSCU-6678901',
    vesselName: 'MSC AURORA',
    voyageNo: 'MSC-2024-115',
    description: '装柜前预冷不足导致运输初期温度偏高',
    iotEvidences: [
      {
        id: 'IOT-EV-601', timestamp: '2025-01-04T02:00:00Z',
        eventType: '温度异常', description: '装柜后前 4 小时温度未达标，5.2°C > 4°C',
        deviceNo: 'IOT-BP-5050', readingValue: '5.2°C', status: 'danger',
      },
      {
        id: 'IOT-EV-602', timestamp: '2025-01-04T08:00:00Z',
        eventType: '温度恢复', description: '冷机加速运转，温度降至 2.8°C',
        deviceNo: 'IOT-BP-5050', readingValue: '2.8°C', status: 'normal',
      },
      {
        id: 'IOT-EV-603', timestamp: '2025-01-05T09:00:00Z',
        eventType: '定损启动', description: '装柜前预冷记录不完整，启动理赔',
        deviceNo: 'IOT-BP-5050', readingValue: '已启动', status: 'warning',
      },
      {
        id: 'IOT-EV-604', timestamp: '2025-01-07T14:00:00Z',
        eventType: '定责确认', description: '装柜前环节责任 40%，赔付 $6,300',
        deviceNo: 'IOT-BP-5050', readingValue: '装柜前40%', status: 'warning',
      },
      {
        id: 'IOT-EV-605', timestamp: '2025-01-10T12:00:00Z',
        eventType: '赔付完成', description: '赔付 $6,300 已到账',
        deviceNo: 'IOT-BP-5050', readingValue: '已赔付', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-008',
    clmNo: 'CLM-2024-0008',
    orderNo: 'ORD-142',
    claimType: 'iot_offline',
    claimAmount: 4200,
    currency: 'USD',
    liabilityResult: 'pre_loading_40',
    status: 'settled_paid',
    createdAt: '2025-01-05T15:00:00Z',
    updatedAt: '2025-01-14T10:00:00Z',
    operatorName: '王强',
    containerNo: 'MSCU-7723456',
    vesselName: 'CMA CGM LIBRA',
    voyageNo: 'CMG-2024-142',
    description: 'IoT设备在装柜阶段未正确绑定，前 8h 无数据',
    iotEvidences: [
      {
        id: 'IOT-EV-701', timestamp: '2025-01-06T01:00:00Z',
        eventType: '设备未绑定', description: '设备 IOT-BP-6090 未在装柜后正常激活',
        deviceNo: 'IOT-BP-6090', readingValue: '未激活', status: 'danger',
      },
      {
        id: 'IOT-EV-702', timestamp: '2025-01-06T09:00:00Z',
        eventType: '设备激活', description: '现场人员手动激活设备，开始采集数据',
        deviceNo: 'IOT-BP-6090', readingValue: '已激活', status: 'warning',
      },
      {
        id: 'IOT-EV-703', timestamp: '2025-01-07T10:00:00Z',
        eventType: '数据补录', description: '缺失 8h 温度数据无法补录，标记为数据缺失',
        deviceNo: 'IOT-BP-6090', readingValue: '缺失', status: 'warning',
      },
      {
        id: 'IOT-EV-704', timestamp: '2025-01-09T11:00:00Z',
        eventType: '定责确认', description: '装柜前环节操作不当，责任 40%',
        deviceNo: 'IOT-BP-6090', readingValue: '装柜前40%', status: 'warning',
      },
      {
        id: 'IOT-EV-705', timestamp: '2025-01-14T10:00:00Z',
        eventType: '赔付完成', description: '赔付 $4,200 已到账',
        deviceNo: 'IOT-BP-6090', readingValue: '已赔付', status: 'normal',
      },
    ],
  },
  {
    id: 'CLM-009',
    clmNo: 'CLM-2024-0009',
    orderNo: 'ORD-167',
    claimType: 'cargo_damage',
    claimAmount: 11200,
    currency: 'USD',
    liabilityResult: 'assessing',
    status: 'settled_rejected',
    createdAt: '2025-01-07T10:00:00Z',
    updatedAt: '2025-01-15T17:00:00Z',
    operatorName: '李娜',
    containerNo: 'MSCU-8832109',
    vesselName: 'MAERSK SELANDIA',
    voyageNo: 'MAE-2024-167',
    description: '目的地仓库验收时发现部分咖啡豆霉变，但包装完好，疑为发货前品质问题',
    iotEvidences: [
      {
        id: 'IOT-EV-801', timestamp: '2025-01-08T08:00:00Z',
        eventType: '货损报告', description: '到仓验收发现咖啡豆霉变，包装完好',
        deviceNo: 'N/A', readingValue: '霉变', status: 'danger',
      },
      {
        id: 'IOT-EV-802', timestamp: '2025-01-08T14:00:00Z',
        eventType: 'IoT数据调取', description: '全程温度 2.8–3.5°C，湿度 65–72%，均在正常范围',
        deviceNo: 'IOT-BP-7030', readingValue: '正常', status: 'normal',
      },
      {
        id: 'IOT-EV-803', timestamp: '2025-01-09T10:00:00Z',
        eventType: '铅封校验', description: '铅封完好，排除运输途中污染',
        deviceNo: 'N/A', readingValue: '完好', status: 'normal',
      },
      {
        id: 'IOT-EV-804', timestamp: '2025-01-12T11:00:00Z',
        eventType: '产地追溯', description: '追溯至产地，发现该批次出厂含水率偏高',
        deviceNo: 'N/A', readingValue: '产地问题', status: 'warning',
      },
      {
        id: 'IOT-EV-805', timestamp: '2025-01-15T17:00:00Z',
        eventType: '拒赔决定', description: '判定为发货前品质问题，不在理赔范围，拒赔',
        deviceNo: 'N/A', readingValue: '拒赔', status: 'danger',
      },
    ],
  },
  {
    id: 'CLM-010',
    clmNo: 'CLM-2024-0010',
    orderNo: 'ORD-198',
    claimType: 'shortage',
    claimAmount: 2800,
    currency: 'USD',
    liabilityResult: 'assessing',
    status: 'settled_rejected',
    createdAt: '2025-01-09T08:30:00Z',
    updatedAt: '2025-01-16T14:00:00Z',
    operatorName: '赵丽',
    containerNo: 'MSCU-9981203',
    vesselName: 'COSCO PRIDE',
    voyageNo: 'COS-2024-198',
    description: '买方声称短少 8 箱，但装柜记录和铅封均正常',
    iotEvidences: [
      {
        id: 'IOT-EV-901', timestamp: '2025-01-10T09:00:00Z',
        eventType: '短装报告', description: '买方声称短少 8 箱',
        deviceNo: 'N/A', readingValue: '短少8箱', status: 'danger',
      },
      {
        id: 'IOT-EV-902', timestamp: '2025-01-10T14:00:00Z',
        eventType: '装柜记录调取', description: '装箱监控显示数量正确，记录完整',
        deviceNo: 'N/A', readingValue: '装箱正常', status: 'normal',
      },
      {
        id: 'IOT-EV-903', timestamp: '2025-01-11T10:00:00Z',
        eventType: '铅封校验', description: '铅封完好，排除运输途中丢失',
        deviceNo: 'N/A', readingValue: '完好', status: 'normal',
      },
      {
        id: 'IOT-EV-904', timestamp: '2025-01-13T11:00:00Z',
        eventType: '买方仓库核查', description: '买方无法提供完整到仓验收记录',
        deviceNo: 'N/A', readingValue: '记录缺失', status: 'warning',
      },
      {
        id: 'IOT-EV-905', timestamp: '2025-01-16T14:00:00Z',
        eventType: '拒赔决定', description: '证据不足，买方无法证明运输环节丢失，拒赔',
        deviceNo: 'N/A', readingValue: '拒赔', status: 'danger',
      },
    ],
  },
];

/** 理赔统计（对齐 PRD 数值） */
export const mockClaimStats: ClaimStats = {
  processingCount: 4,
  assessingCount: 2,
  approvingCount: 1,
  pendingPayoutCount: 1,
  monthlySettledCount: 7,
  monthlyPaidCount: 5,
  monthlyRejectedCount: 2,
  totalPayoutAmount: 38500,
  avgResponseTime: 18,
};

/** 从工单数组派生理赔统计数据 */
export function deriveClaimStats(
  workOrders: ClaimWorkOrder[],
  now: Date = new Date(),
): ClaimStats {
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  const processing = workOrders.filter((w) =>
    ['processing', 'assessing', 'approving', 'pending_payout'].includes(w.status),
  );
  const settled = workOrders.filter((w) =>
    ['settled_paid', 'settled_rejected'].includes(w.status),
  );
  const monthlySettled = settled.filter((w) => {
    const d = new Date(w.updatedAt);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });

  return {
    processingCount: processing.length,
    assessingCount: processing.filter((w) => w.status === 'assessing').length,
    approvingCount: processing.filter((w) => w.status === 'approving').length,
    pendingPayoutCount: processing.filter((w) => w.status === 'pending_payout').length,
    monthlySettledCount: monthlySettled.length,
    monthlyPaidCount: monthlySettled.filter((w) => w.status === 'settled_paid').length,
    monthlyRejectedCount: monthlySettled.filter((w) => w.status === 'settled_rejected').length,
    totalPayoutAmount: workOrders
      .filter((w) => w.status === 'settled_paid')
      .reduce((sum, w) => sum + (w.claimAmount ?? 0), 0),
    avgResponseTime:
      workOrders.length === 0
        ? 0
        : Math.round(
            workOrders.reduce((sum, w) => sum + (now.getTime() - new Date(w.createdAt).getTime()), 0) /
              workOrders.length /
              3600000,
          ),
  };
}
